import React, { useState, useEffect, useRef } from 'react';
import {
  adminGetDiscountApplications,
  adminReviewDiscount,
  getDocumentSignedUrl,
  getDiscountStatusLabel,
  getDiscountStatusColor,
} from '../../services/discountService';
import {
  DiscountWithApplication,
  getDiscountReasonText,
} from '../../types/discount';
import {
  EyeIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const DiscountManagement: React.FC = () => {
  const [discounts, setDiscounts] = useState<DiscountWithApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountWithApplication | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadDiscounts();
  }, [statusFilter]);

  const loadDiscounts = async () => {
    setLoading(true);
    const data = await adminGetDiscountApplications(statusFilter);
    setDiscounts(data);
    setLoading(false);
  };

  const handleReview = async (discountId: string) => {
    if (!window.confirm('감면 신청을 확인 처리하시겠습니까?')) return;

    setProcessingId(discountId);
    const result = await adminReviewDiscount(discountId);
    if (result.success) {
      await loadDiscounts();
    } else {
      alert(result.error || '처리 중 오류가 발생했습니다.');
    }
    setProcessingId(null);
  };

  const handleDownloadFile = async (filePath: string, fileName: string) => {
    try {
      const signedUrl = await getDocumentSignedUrl(filePath);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const openDetail = (discount: DiscountWithApplication) => {
    setSelectedDiscount(discount);
    setShowDetailModal(true);
  };

  const filteredDiscounts = discounts.filter(d => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      d.student_name?.toLowerCase().includes(term) ||
      d.program_info?.courseName?.toLowerCase().includes(term) ||
      d.user_info?.phone?.includes(term) ||
      d.application?.application_number?.toLowerCase().includes(term)
    );
  });

  const submittedCount = discounts.filter(d => d.status === 'submitted').length;
  const reviewedCount = discounts.filter(d => d.status === 'reviewed').length;

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">감면 관리</h1>
        <p className="text-gray-500 mt-1">수강생 감면 신청을 관리합니다.</p>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">접수 대기</p>
          <p className="text-3xl font-bold text-amber-500 mt-1">{submittedCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">확인 완료</p>
          <p className="text-3xl font-bold text-green-500 mt-1">{reviewedCount}</p>
        </div>
      </div>

      {/* 검색/필터 */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="이름, 프로그램명, 연락처 검색"
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="all">전체</option>
          <option value="submitted">접수됨</option>
          <option value="reviewed">확인완료</option>
        </select>
      </div>

      {/* 목록 */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">로딩 중...</div>
      ) : filteredDiscounts.length === 0 ? (
        <div className="text-center py-12 text-gray-400">감면 신청 내역이 없습니다.</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">신청 정보</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">수강생</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">감면 유형</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">감면 사유</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">신청일</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">작업</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDiscounts.map(discount => (
                  <tr key={discount.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {discount.program_info?.courseName || '-'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {discount.class_info?.class_name || ''}
                      </div>
                      <div className="text-xs text-gray-400">
                        {discount.application?.application_number || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{discount.student_name}</div>
                      <div className="text-xs text-gray-500">{discount.user_info?.phone || ''}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        discount.discount_type === '100%'
                          ? 'bg-red-50 text-red-600'
                          : 'bg-blue-50 text-blue-600'
                      }`}>
                        {discount.discount_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                      {discount.discount_reason_text}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${getDiscountStatusColor(discount.status)}`}>
                        {getDiscountStatusLabel(discount.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(discount.created_at).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetail(discount)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="상세 보기"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {discount.status === 'submitted' && (
                          <button
                            onClick={() => handleReview(discount.id)}
                            disabled={processingId === discount.id}
                            className="p-1.5 text-green-400 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                            title="확인 완료"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 상세 보기 모달 — 양식 형태 + 인쇄 가능 */}
      {showDetailModal && selectedDiscount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 (인쇄 시 숨김) */}
            <div className="flex items-center justify-between p-4 border-b print:hidden">
              <h3 className="text-lg font-semibold text-gray-900">감면 신청서 상세</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <PrinterIcon className="h-4 w-4" />
                  인쇄
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* 신청서 양식 (인쇄 대상) */}
            <div ref={printRef} className="p-6 print:p-4">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">수강료 감면 신청서</h2>
              </div>

              {/* 인적사항 */}
              <table className="w-full border-collapse border border-gray-400 mb-4 text-sm">
                <tbody>
                  <tr>
                    <td rowSpan={4} className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium w-24 text-center">인적사항</td>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium w-24">수강과목</td>
                    <td colSpan={3} className="border border-gray-400 px-3 py-2">{selectedDiscount.program_info?.courseName || '-'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium">수강생 성명</td>
                    <td className="border border-gray-400 px-3 py-2">{selectedDiscount.student_name}</td>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium w-24">보호자 성명</td>
                    <td className="border border-gray-400 px-3 py-2">{selectedDiscount.guardian_name || '-'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium">생년월일</td>
                    <td className="border border-gray-400 px-3 py-2">{selectedDiscount.birthdate || '-'}</td>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium">보호자 휴대전화</td>
                    <td className="border border-gray-400 px-3 py-2">{selectedDiscount.guardian_phone || '-'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium">주소</td>
                    <td className="border border-gray-400 px-3 py-2">{selectedDiscount.address || selectedDiscount.user_info?.address || '-'}</td>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium">수강료 환불계좌</td>
                    <td className="border border-gray-400 px-3 py-2">
                      {selectedDiscount.bank_name ? `${selectedDiscount.bank_name} ${selectedDiscount.account_number}` : '-'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* 개인정보 동의 */}
              <table className="w-full border-collapse border border-gray-400 mb-4 text-sm">
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium w-24 text-center">개인정보 수집 및 이용 동의</td>
                    <td className="border border-gray-400 px-3 py-2">
                      동의 {selectedDiscount.privacy_consent ? '☑' : '☐'} &nbsp;&nbsp; 동의안함 {!selectedDiscount.privacy_consent ? '☑' : '☐'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* 감면 사유 */}
              <table className="w-full border-collapse border border-gray-400 mb-4 text-sm">
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium w-24 text-center align-top" rowSpan={2}>감면신청</td>
                    <td className="border border-gray-400 px-3 py-2">
                      <div className="font-medium text-gray-600 mb-1">{selectedDiscount.discount_type} 감면</div>
                      <div>{selectedDiscount.discount_reason_code}. {selectedDiscount.discount_reason_text}</div>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* 이용자 확인 */}
              <table className="w-full border-collapse border border-gray-400 mb-4 text-sm">
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 bg-gray-50 font-medium w-24 text-center">이용자 확인사항</td>
                    <td className="border border-gray-400 px-3 py-2">
                      본인은 감면 등록결과에 따른 것을 동의하며 프로그램에 성실하게 참여할 것을 약속합니다. {selectedDiscount.user_confirmation ? '☑ 확인' : '☐ 미확인'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* 신청 정보 */}
              <div className="text-sm text-center text-gray-600 mt-6 mb-2">
                {new Date(selectedDiscount.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="text-sm text-center text-gray-600 mb-4">
                작성자: {selectedDiscount.student_name}
              </div>
              <div className="text-center font-semibold text-gray-800">
                동두천시글로벌인재교육센터장 귀하
              </div>

              {/* 첨부서류 (인쇄 시에도 파일명 표시) */}
              {selectedDiscount.attachments && selectedDiscount.attachments.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-300">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">첨부서류</h4>
                  <div className="space-y-2">
                    {selectedDiscount.attachments.map((file: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <button
                          onClick={() => handleDownloadFile(file.url, file.name)}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 print:hidden"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          다운로드
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 관리자 메모 */}
              {selectedDiscount.admin_note && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg print:hidden">
                  <p className="text-sm text-blue-800"><span className="font-medium">관리자 메모:</span> {selectedDiscount.admin_note}</p>
                </div>
              )}
            </div>

            {/* 모달 하단 (인쇄 시 숨김) */}
            <div className="flex justify-between items-center p-4 border-t bg-gray-50 print:hidden">
              <div>
                <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${getDiscountStatusColor(selectedDiscount.status)}`}>
                  {getDiscountStatusLabel(selectedDiscount.status)}
                </span>
              </div>
              <div className="flex gap-2">
                {selectedDiscount.status === 'submitted' && (
                  <button
                    onClick={() => {
                      handleReview(selectedDiscount.id);
                      setShowDetailModal(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    확인 완료
                  </button>
                )}
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountManagement;
